/** Words on the People pages: main People (everyone) and each group's People. */

// ---- main People: one row per person ({amount} etc. are filled in by the screen)
/**
 * Amounts are plain figures from the person's side, like a bank app: minus and red is what they
 * owe, plus and green is a credit (tutoring pay, money you owe them).
 */
/** One part of a row's summary ({label} = "I can't fly", {amount} = "−$110.63"), and what joins them. */
export const SUMMARY_PART = "{label} {amount}";
export const SUMMARY_JOIN = " · ";
/** The overall total under the summary ({amount}). Empty hides it (the amount on the right says it). */
export const PEOPLE_TOTAL_TEXT = "";
/** A row with nothing anywhere. */
export const PEOPLE_PAID_UP_EVERYWHERE = "Paid up everywhere";

// ---- one person's card, the same on main People and a group's People ({amount} = "$12.00")
/** A group row's one-line summary (your own view of the group). {since} = the next line or empty. */
export const CARD_YOU_TEXT = "Your share {amount}";
/** Someone who owes: when their oldest unpaid item is from ({since} = the next line). */
export const CARD_OWES_TEXT = "{since}";
export const CARD_OLDEST_TEXT = "Oldest unpaid {date} · {days} {dayword}";
export const CARD_DAY = "day";
export const CARD_DAYS = "days";
/** Someone you owe (the amount on the right says how much). Empty hides it. */
export const CARD_OWED_TEXT = "";
export const CARD_PAID_TEXT = "Paid up{last}";
export const CARD_LAST_PAID = " · last paid {when}";
export const CARD_NONE_TEXT = "No items yet";
/** The same, for a member looking at the group ({treasurer} = who runs it). Empty hides it. */
export const CARD_M_TREASURER_TEXT = "{treasurer} runs this group";
export const CARD_M_OWES = "";
export const CARD_M_NONE = "Nothing yet";
export const CARD_M_PAID_UP = "Paid up";

/** Label of the part of the card that says where the balance comes from ({name}). */
export const CARD_SOURCES_LABEL = "Where {name}'s balance comes from";

// ---- the rows of one source (each source's rows add up exactly to its balance)
export const ROW_ITEMS = "Their items";
export const ROW_YOUR_ITEMS = "Your items";
export const ROW_FRONTED = "Paid stores for the group";
export const ROW_PAID = "Paid you";
export const ROW_PAID_TREASURER = "Paid {treasurer}";
export const ROW_EARNED = "Tutoring pay earned";
export const ROW_EARNED_SELF = "Your tutoring pay";
export const ROW_PAID_OUT = "You paid them";
export const ROW_PAID_OUT_SELF = "Paid to you";
export const ROW_BOUGHT = "They bought";
export const ROW_BOUGHT_SELF = "You bought";
export const ROW_CHARGED = "Lessons and charges";
export const ROW_FAMILY_PAID = "Paid you";
export const ROW_FAMILY_PAID_SELF = "You paid";
export const ROW_BALANCE = "Balance";
/** "left" after an item still partly unpaid. */
export const UNPAID_LEFT = " left";

// ---- the four totals, in this order, on every view of a person (a view shows the ones that apply)
/** Their items: money they spent (in every group on People, in this group inside a group). */
export const TOTAL_SPENT = "Total spent";
/** What they owe in groups, all added up (main People only; inside a group it's the balance). */
export const TOTAL_GROUPS = "All groups";
/** Their tutoring: pay earned (a tutor) or lessons (a family). */
export const TOTAL_TUTORING = "Tutoring";
/** What's left after everything above. */
export const TOTAL_LEFT = "Balance left";

// ---- the statement: everything, newest first, with the balance after each line
/** Heading. Empty hides it. */
export const STMT_TITLE = "History";
/** Label of the list ({name}). */
export const STMT_LABEL = "{name}'s history";
/** How many lines show before "Show all". */
export const STMT_SHOWN = 12;
export const STMT_SHOW_ALL = "Show all {count}";
/** Every line: where it's from and what it was ({source} = "I can't fly" or "Tutoring pay"). */
export const STMT_LINE = "{source} · {what}";
/** Each kind of line ({name}, {note}, {hours}, {time} = ", 16:00–17:00" or empty, {student} = " · Yulin Wang" or empty). */
export const STMT_ITEM = "{name}";
export const STMT_FRONTED = "Paid the store: {name}";
export const STMT_PAYMENT = "Paid you{note}";
export const STMT_PAYMENT_OUT = "You paid them{note}";
export const STMT_PAYMENT_SELF = "You paid{note}";
export const STMT_PAYMENT_OUT_SELF = "Paid to you{note}";
/** Joins a payment's note: " (Zelle)". */
export const STMT_NOTE = " ({note})";
export const STMT_LESSON = "{hours} lesson{time}{student}";
export const STMT_LESSON_TIME = ", {start}–{end}";
export const STMT_LESSON_STUDENT = " · {student}";
export const STMT_PAYOUT = "You paid them ({method})";
export const STMT_PAYOUT_SELF = "Paid to you ({method})";
export const STMT_BOUGHT = "Bought: {name}";
export const STMT_PURCHASE = "{hours} prepaid";
export const STMT_CHARGE = "{name}";
export const STMT_FAMILY_PAYMENT = "Paid you{note}";
export const STMT_FAMILY_PAYMENT_SELF = "You paid{note}";
/** Payout and payment methods. */
export const METHOD_ZELLE = "Zelle";
export const METHOD_CASH = "Cash";
export const METHOD_CARD = "Card";
export const METHOD_OTHER = "Other";
/** Hover text on lines you can open. */
export const STMT_EDIT_TITLE = "Change or remove";

// ---- a group's People: badges next to a name
export const BADGE_TREASURER = "Club treasurer";
export const BADGE_ACCOUNT = "Has an account";
export const BADGE_INVITED = "Invited";
export const BADGE_NO_ACCOUNT = "No account";
export const BADGE_YOU = "You";

// ---- a group's People: what you can do with someone ({name} = their full name)
export const ACT_EDIT = "Edit name and color";
export const ACT_LOG_PAYMENT = "Log payment";
export const ACT_COPY_MESSAGE = "Copy message";
export const ACT_SEE_AS = "See what they see";
export const ACT_MAKE_MEMBER = "Make a member again";
export const ACT_MAKE_TREASURER = "Make club treasurer";
export const ACT_MAKE_TREASURER_TITLE =
  "They see and change everything in this group (only this one)";
export const ACT_REMOVE_ACCESS = "Remove access";
export const ACT_NEW_INVITE = "Send a new invite";
export const ACT_INVITE = "Invite to make an account";
/** Editing someone. */
export const EDIT_NAME_LABEL = "Name";
export const EDIT_COLOR_LABEL = "Color";
export const EDIT_ANY_COLOR = "Any color";
export const EDIT_NEED_LAST_NAME = "Add a last name too.";
export const EDIT_SAVE = "Save";
export const EDIT_CANCEL = "Cancel";
export const EDIT_ARCHIVE = "Archive";
/** The groups an account sees ({name} = the account's name). */
export const ACCT_GROUPS_TITLE = "Groups {name} sees";
export const ACCT_GROUPS_NOTE = "";
export const ACCT_SAVING = " saving";
/** Linking an account to someone ({name} = the person). */
export const ACCT_TITLE = "Account";
export const ACCT_FOR_LABEL = "Account for {name}";
export const ACCT_CHOOSE = "Choose an account";
export const ACCT_LINK = "Link account";
/** Hover text on the balance-over-time line. */
export const SPARK_TITLE = "Balance over the last 16 weeks";

// ---- main People: one person, one row (who is who; nothing is joined until you say so)
/** Above the possible matches, and above the take-apart buttons. Empty hides them. */
export const IDENT_SUGGEST_NOTE = "";
export const IDENT_SPLIT_NOTE = "";
/** {name} = the record's full name, {label} = its group or Tutoring. */
export const IDENT_SPLIT_BUTTON = "Not {name} in {label}";
export const IDENT_SPLIT_TOAST = "{name} is on their own again";
export const IDENT_SAME_AS = "Same person as";
export const IDENT_PICK = "Pick someone";
/** An option: {name} ({groups}). */
export const IDENT_OPTION = "{name} ({groups})";
export const IDENT_NO_GROUPS = "no groups";
export const IDENT_MERGE = "Put together";

// ---- main People: why a person's lines are together (owner only, for checking a card)
/** Show the "why these are together" list on an opened card. */
export const SHOW_JOIN_TRACE = true;
/** Its title (tap to open). Empty hides the whole list. */
export const TRACE_TITLE = "Why these are together";
/** One line on the card: {label} = its group or Tutoring, {name} = the name typed there. */
export const TRACE_LINE = "{label} · {name}";
/** Said for the line the others were joined to. */
export const TRACE_FIRST = "first line";
/** {links} = the links that joined it, joined by TRACE_JOIN. */
export const TRACE_BY = "joined by {links}";
export const TRACE_JOIN = ", ";
/** Each kind of link: {detail} = which group, account, person or record. */
export const TRACE_LINK_YOU = "you in {detail}";
export const TRACE_LINK_ACCOUNT = "account {detail}";
export const TRACE_LINK_IDENTITY = "same person ({detail})";
export const TRACE_LINK_TUTOR = "tutor tied to {detail}";
/** A link that was left out because it would put your own lines with someone else's. */
export const TRACE_LEFT_OUT = "Left out: {links} (would put you with someone else)";
